/**
 * YouTubePublishForm — single-page form for submitting a YouTube publish job.
 *
 * Supports two modes:
 *   end_to_end    Upload a new video from a local file path.
 *   edit_existing Video already on YouTube; add/update metadata + localizations.
 *
 * On submit calls submitPublishJob and fires onJobStarted(job_id).
 *
 * Accepts an optional `preset` prop (forwarded from ResultDisplay via
 * YouTubeUploadTab) to pre-populate fields after pipeline generation.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { ChevronDown, ChevronUp, FolderOpen, Sparkles, X } from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import {
  submitPublishJob,
  getLanguages,
  generateMetadataFromVideo,
  subscribeToMetadataFromVideoEvents,
  getVideoLanguageStatus,
  getGenerationManifest,
} from '../../services/youtubePublishApi';
import type {
  YouTubePublishRequest,
  YouTubePublishMode,
  YouTubeNarrationSource,
  YouTubeLanguageConfig,
  SubmitPublishJobResponse,
  VideoLanguageStatus,
  GenerationManifest,
} from '../../services/youtubePublishApi';
import { useRecentGenerations } from '../../hooks/useRecentGenerations';
import { YouTubeThumbnailPanel } from './YouTubeThumbnailPanel';
import type { YouTubeLanguageEntry } from '../../services/youtubePublishApi';
import type { YouTubeChannelVideo } from '../../services/youtubePublishApi';
import { YouTubeLanguageSelector } from '../common/LanguageSelector';
import { SegmentedPicker, CompactSelect } from '../common';
import { LLMModelSelector } from '../selectors';
import { useLLMModels } from '../../hooks/useLLMModels';
import { TokenEstimateCard } from '../tokens/TokenEstimateCard';
import {
  YOUTUBE_VIDEO_CATEGORIES,
  DEFAULT_VIDEO_CATEGORY_ID,
} from '../../constants/youtubeCategories';
import { DEFAULT_VALUES, LLM_PROVIDERS } from '../../constants';
import { STORAGE_KEYS } from '../../constants/storage';
import { ChannelVideoPicker } from './ChannelVideoPicker';
import type { YouTubePublishPreset } from './types';
import { isDesktopApp } from '../../utils/apiMode';
import { selectFolder, selectVideoFile } from '../../utils/electronBridge';
import { COST_GATE_OPEN, type CostGateStatus } from '../../utils/costGate';

interface YouTubePublishFormProps {
  channel_id: string;
  onJobStarted: (job_id: string) => void;
  preset?: YouTubePublishPreset | null;
  // Owned by the parent (YouTubeUploadTab) so it survives this component's
  // unmount/remount when a job starts/finishes -- see YouTubeUploadTab.tsx.
  mode: YouTubePublishMode;
  onModeChange: (mode: YouTubePublishMode) => void;
}

const MODES: Array<{ value: YouTubePublishMode; labelKey: string }> = [
  { value: 'end_to_end', labelKey: 'youtube.publish.modeEndToEnd' },
  { value: 'edit_existing', labelKey: 'youtube.publish.modeEditExisting' },
];

const VISIBILITIES: Array<'private' | 'unlisted' | 'public'> = ['private', 'unlisted', 'public'];
const AUDIENCES: Array<'kids' | 'not_kids'> = ['kids', 'not_kids'];

// Recommended SEO localization model, single-sourced from the backend
// MARKETING_TEXT_MODEL via the generated constants pipeline (gemini-2.5-flash:
// paid, reliable, strongly multilingual, cheap). Preselected by default; the
// user's explicit override is persisted and wins on subsequent visits.
const RECOMMENDED_SEO_MODEL = DEFAULT_VALUES.llm_model_seo;

/**
 * Per-language SEO override. Populated only from a picked generation's persisted
 * localizations; flows into title_override/description_override on submit so the
 * publish job reuses them instead of re-localizing. The interactive SEO preview
 * was removed — the orchestrator auto-localizes any language without an override.
 */
interface SeoEdit {
  title: string;
  description: string;
  hashtags: string;
}

export function YouTubePublishForm({
  channel_id,
  onJobStarted,
  preset,
  mode,
  onModeChange,
}: YouTubePublishFormProps) {
  const { t } = useTranslation();

  // Core fields
  const [videoFilePath, setVideoFilePath] = useState('');
  const [videoUrl, setVideoUrl] = useState('');
  const [selectedVideoId, setSelectedVideoId] = useState<string | null>(null);
  const [sourceTitle, setSourceTitle] = useState('');
  const [sourceDescription, setSourceDescription] = useState('');
  // Source language defaults to "auto" (never a silent 'en'). It auto-fills from
  // the selected video's declared audio language when available. This single
  // field is also the video's default/original language at upload time -- there
  // is no separate "default language" field (it was redundant).
  const [sourceLanguage, setSourceLanguage] = useState('auto');

  // Which target languages already exist on the selected video (#5: fill gaps).
  const [videoLangStatus, setVideoLangStatus] = useState<VideoLanguageStatus | null>(null);

  // Upload metadata
  const [categoryId, setCategoryId] = useState(DEFAULT_VIDEO_CATEGORY_ID);
  const [visibility, setVisibility] = useState<'private' | 'unlisted' | 'public'>('private');
  const [audience, setAudience] = useState<'kids' | 'not_kids'>('not_kids');

  // Assets root (needed when any lang requests captions/thumbnail)
  const [jobAssetsRoot, setJobAssetsRoot] = useState('');

  // Publish-from-generation picker (end_to_end): pick a finished local run and
  // pre-fill video + captions + base SEO + target languages from one folder.
  // Recent-generations loading lives in useRecentGenerations (owns the list +
  // its cancellable load effect; only loads in end_to_end mode).
  const { recentGenerations, loadError: recentGenerationsLoadError } = useRecentGenerations(mode);
  const [selectedGenerationDir, setSelectedGenerationDir] = useState('');
  const [generationLoadError, setGenerationLoadError] = useState<string | null>(null);
  const [videoFilePickerError, setVideoFilePickerError] = useState<string | null>(null);
  const [pickedManifest, setPickedManifest] = useState<GenerationManifest | null>(null);
  // Source of the base title/description the publish job generates (then localizes):
  //   'none'     -> don't generate; use the provided/source title & description.
  //   'audio'    -> speech-to-text of the video's audio (edit_existing; DESKTOP ONLY).
  //   'captions' -> the published caption track (edit_existing) or, in end_to_end,
  //                 the generation run folder's narration.
  // Default audio on desktop / captions on web (audio is disabled on web). The
  // per-mode effect below resets this when the mode changes; handleSelectGeneration
  // opts a picked end_to_end run in.
  const [narrationSource, setNarrationSource] = useState<YouTubeNarrationSource>(
    isDesktopApp() ? 'audio' : 'captions'
  );

  // Language list from backend
  const [languages, setLanguages] = useState<YouTubeLanguageEntry[]>([]);
  const [selectedLangCodes, setSelectedLangCodes] = useState<string[]>([]);

  // "What to apply" — all checked by default
  const [applySeo, setApplySeo] = useState(true); // Localized title + description
  const [applyCaptions, setApplyCaptions] = useState(true); // Subtitles / captions
  const [applyThumbnail, setApplyThumbnail] = useState(true); // Custom thumbnail (whole video)
  // Hashtags are always appended to the description when SEO is on (the SEO model
  // generates them as part of the localized description); no separate toggle.
  // edit_existing only: re-upload captions for languages already on the video
  // (maps to on_existing_locale='overwrite'; default skip leaves them untouched).
  const [overwriteCaptions, setOverwriteCaptions] = useState(false);
  // end_to_end "regenerate subtitles": re-translate the run's source SRT into the
  // chosen languages (instead of using the run's existing SRTs / skipping). Lets
  // the user publish in languages the run never produced. Off by default — RULE:
  // existing subtitles are never silently regenerated.
  const [regenerateSubtitles, setRegenerateSubtitles] = useState(false);
  const [regenerateLangCodes, setRegenerateLangCodes] = useState<string[]>([]);

  // Submit state
  const [submitting, setSubmitting] = useState(false);
  const [submitResult, setSubmitResult] = useState<SubmitPublishJobResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Preset banner
  const [showPresetBanner, setShowPresetBanner] = useState(false);

  // AI metadata-from-video (download -> STT -> SEO) state
  const [aiGenerating, setAiGenerating] = useState(false);
  const [aiMessage, setAiMessage] = useState<string | null>(null);
  const [aiError, setAiError] = useState<string | null>(null);
  const aiUnsubscribeRef = useRef<(() => void) | null>(null);

  // ---------------------------------------------------------------------------
  // Thumbnail panel state — YouTube uses ONE thumbnail per video, so we
  // generate a single thumbnail (from the source title/description), not one
  // per language.
  // ---------------------------------------------------------------------------
  // Open by default so the image provider/model config is visible the moment the
  // user enables "Custom thumbnail" (collapsed-by-default made it look missing).
  const [thumbPanelOpen, setThumbPanelOpen] = useState(true);

  // Per-language SEO overrides, reused from a picked generation's persisted
  // localizations (the interactive SEO preview was removed).
  const [seoEdits, setSeoEdits] = useState<Record<string, SeoEdit>>({});

  // Whether a custom thumbnail was actually generated + saved for this job. Used
  // to warn before submit when "Custom thumbnail" is enabled but none was made
  // (the publish job uploads no thumbnail; it never generates one server-side).
  const [thumbnailSavedPath, setThumbnailSavedPath] = useState<string | null>(null);

  // Translation engine for title/description localization AND captions.
  // Off by default: local NLLB-200 (free, no dependency on OpenRouter being up
  // or funded). Turning this on opts into the paid LLM path below, where the
  // user must explicitly pick a model -- no hard-coded default, no silent LLM
  // spend, and no silent quality loss if the LLM account runs out of credits
  // (diagnosed 2026-07-26: it did, and every language silently degraded).
  const [useAiTranslation, setUseAiTranslation] = useState(false);
  // Gates submit alongside seoModel below: TokenEstimateCard reports this via
  // onEstimateChange once it resolves the live /api/v1/tokens/estimate cost.
  // Defaults to true so it never blocks submit before the AI toggle is even on.
  const [translationCostGate, setTranslationCostGate] = useState<CostGateStatus>(COST_GATE_OPEN);

  // SEO/captions LLM provider + model. Same three providers create_llm_provider()
  // supports everywhere else in the app (openrouter/ollama/runpod) -- this used
  // to be hard-coded to openrouter here only, which was an oversight (OpenRouter
  // outages/quota exhaustion have no bearing on Ollama/RunPod availability), not
  // a real platform constraint. We use the hook ONLY for the model LIST and hold
  // our own selection state (its internal default is the FREE rate-limited
  // model, unsuitable for SEO — we ignore it).
  const [llmProvider, setLlmProvider] = useState<string>(DEFAULT_VALUES.LLM_PROVIDER);
  const llmProviderOptions = useMemo(
    () =>
      LLM_PROVIDERS.map((provider) => ({
        value: provider.value,
        label: provider.label,
        disabled: provider.disabled,
      })),
    []
  );
  const { availableModels: seoModels } = useLLMModels(llmProvider, undefined, {
    context: 'chat',
    enabled: (applySeo || applyCaptions) && useAiTranslation,
  });
  // Start from the user's last persisted choice so it "sticks" across sessions;
  // otherwise the recommended default is preselected once the list loads (below).
  const [seoModel, setSeoModel] = useState<string>(
    () => localStorage.getItem(STORAGE_KEYS.YOUTUBE_SEO_MODEL) ?? ''
  );

  // Persist ONLY explicit user changes (not the auto preselect below) so the
  // recommended default never becomes "sticky" until the user actually chooses.
  const handleSeoModelChange = useCallback((model: string): void => {
    setSeoModel(model);
    if (model) localStorage.setItem(STORAGE_KEYS.YOUTUBE_SEO_MODEL, model);
  }, []);

  // Switching provider invalidates the current model pick immediately (no
  // stale cross-provider model id lingering in the picker while the new
  // provider's list loads) -- the preselect effect below then either
  // re-recommends (openrouter) or leaves it for the user to pick explicitly.
  const handleLlmProviderChange = useCallback((provider: string): void => {
    setLlmProvider(provider);
    setSeoModel('');
  }, []);

  // Preselect the recommended model once the list is available — unless the user
  // already has a still-available selection (their persisted choice wins). The
  // recommended model stays selected only until the user picks another. Only
  // "openrouter" has a curated recommendation (RECOMMENDED_SEO_MODEL is an
  // OpenRouter-format model id); other providers just leave seoModel empty for
  // the user to pick explicitly (the "pick a model" hint below covers that).
  useEffect(() => {
    if ((!applySeo && !applyCaptions) || !useAiTranslation || seoModels.length === 0) return;
    const isSelectable = (v: string): boolean =>
      seoModels.some((m) => m.value === v && m.disabled !== true);
    if (seoModel && isSelectable(seoModel)) return;
    if (isSelectable(RECOMMENDED_SEO_MODEL)) {
      setSeoModel(RECOMMENDED_SEO_MODEL);
    } else if (seoModel) {
      // Stale selection from a different provider's model list -- clear it
      // rather than silently keep a model id the current provider doesn't have.
      setSeoModel('');
    }
  }, [applySeo, applyCaptions, useAiTranslation, seoModels, seoModel]);

  // Cost preview for the "youtube_translation" estimator (title+description
  // localization only -- caption translation cost isn't knowable at form-fill
  // time, since the source caption text lives on the published video / a
  // pipeline run folder and is only fetched when the publish job actually
  // runs; see content_estimator.py's _estimate_youtube_translation docstring).
  const translationEstimateParams = useMemo(
    () => ({
      title: sourceTitle,
      description: sourceDescription,
      target_language_count: selectedLangCodes.length,
      model: seoModel || undefined,
    }),
    [sourceTitle, sourceDescription, selectedLangCodes.length, seoModel]
  );

  // The selector uses '' transiently to mean "pick manually"; never send that or
  // a blank downstream — coerce to the "auto" sentinel (backend detects it).
  const effectiveSourceLanguage = sourceLanguage && sourceLanguage !== '' ? sourceLanguage : 'auto';

  // Mirror the backend's edit_existing metadata no-op guard so the user gets
  // immediate, actionable feedback instead of a failed job: when every target
  // equals the source language, SEO is on, no narration source is selected, and
  // no new title/description (or override) is provided, the job would republish
  // the video's current metadata onto itself (and the backend rejects it).
  const wouldRepublishNoop =
    mode === 'edit_existing' &&
    applySeo &&
    narrationSource === 'none' &&
    selectedLangCodes.length > 0 &&
    selectedLangCodes.every((c) => c === effectiveSourceLanguage) &&
    sourceTitle.trim() === '' &&
    sourceDescription.trim() === '' &&
    Object.keys(seoEdits).length === 0;

  // No SEO confirm-gate: the localized preview is shown inline and editable, and
  // edits flow straight into the request. Submit needs target languages and,
  // when SEO localization is on AND the user opted into paid AI translation, an
  // explicitly-chosen model (no silent default). NLLB (the default) needs no model.
  const submitDisabled =
    submitting ||
    selectedLangCodes.length === 0 ||
    ((applySeo || applyCaptions) && useAiTranslation && !seoModel) ||
    (applySeo && useAiTranslation && translationCostGate !== 'affordable') ||
    wouldRepublishNoop;

  // ---------------------------------------------------------------------------
  // Load language list
  // ---------------------------------------------------------------------------
  useEffect(() => {
    getLanguages()
      .then(setLanguages)
      .catch(() => {
        // Non-fatal; form is still usable without language list
      });
  }, []);

  // ---------------------------------------------------------------------------
  // Apply preset when it arrives
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!preset) return;
    setVideoFilePath(preset.videoFilePath);
    setJobAssetsRoot(preset.jobAssetsRoot);
    setSourceTitle(preset.sourceTitle);
    setSourceDescription(preset.sourceDescription);
    setSourceLanguage(preset.sourceLanguage);
    setShowPresetBanner(true);
    // A new preset replaces the job context: drop any reused SEO overrides and
    // the prior thumbnail-saved marker.
    setSeoEdits({});
    setThumbnailSavedPath(null);
  }, [preset]);

  // ---------------------------------------------------------------------------
  // Publish-from-generation: load recent finished local runs
  // ---------------------------------------------------------------------------
  // Apply a finished generation: pre-fill video + captions + base SEO + targets.
  // Apply the manifest's SEO (base + persisted per-language localizations) to the
  // form. Localizations flow into seoEdits so the publish request reuses them
  // (title_override/description_override) instead of re-localizing.
  const applyManifestSeo = (m: GenerationManifest): void => {
    if (m.title) setSourceTitle(m.title);
    // Apply the manifest's base description verbatim, including the empty case:
    // m.description is null only when the run persisted no base description (the
    // backend then generates it from narration). Using a falsy check here dropped
    // a real empty string and left a stale value — fixed by honouring the null
    // contract directly.
    setSourceDescription(m.description ?? '');
    const locEntries = Object.entries(m.localizations);
    if (locEntries.length > 0) {
      setSeoEdits(
        Object.fromEntries(
          locEntries.map(([lang, v]) => [
            lang,
            { title: v.title, description: v.description, hashtags: '' },
          ])
        )
      );
    }
  };

  const handleSelectGeneration = async (runDir: string): Promise<void> => {
    setSelectedGenerationDir(runDir);
    setGenerationLoadError(null);
    if (!runDir) {
      setPickedManifest(null);
      return;
    }
    try {
      const m = await getGenerationManifest(runDir);
      setPickedManifest(m);
      if (m.video_path) setVideoFilePath(m.video_path);
      setJobAssetsRoot(m.run_dir);
      if (m.source_language) {
        setSourceLanguage(m.source_language);
      }
      // Auto-select the union of audio + subtitle languages so the user gets SEO
      // in every language the video already carries (audio = source_language;
      // subtitles = available_languages + caption tracks) without extra clicks.
      const unionLangs = Array.from(
        new Set(
          [m.source_language, ...m.available_languages, ...Object.keys(m.captions)].filter(Boolean)
        )
      );
      if (unionLangs.length > 0) setSelectedLangCodes(unionLangs);
      applyManifestSeo(m);
      // Opt-in to generating SEO from the run's narration during publish, on by
      // default when the run can do it and doesn't already have SEO persisted.
      // For end_to_end the narration is the run folder; 'captions' is the on value.
      setNarrationSource(m.validation.valid_for_seo && !m.has_seo ? 'captions' : 'none');
      setShowPresetBanner(true);
      // New run context: a previously generated thumbnail no longer applies.
      setThumbnailSavedPath(null);
    } catch (e) {
      setPickedManifest(null);
      setGenerationLoadError(
        e instanceof Error ? e.message : t('youtube.publish.fromGeneration.loadError')
      );
    }
  };

  // A finished generation was handed over (ResultDisplay "Publish"): its run
  // folder is the same thing the picker selects, so resolve it through the same
  // manifest path -- persisted SEO, languages and the narration source arrive
  // pre-filled with no extra click. The preset effect above has already set the
  // video path / title as the fallback for a folder that has no manifest.
  useEffect(() => {
    if (!preset?.jobAssetsRoot) return;
    void handleSelectGeneration(preset.jobAssetsRoot);
    // Keyed on the preset only: handleSelectGeneration is recreated every render
    // and re-running it would overwrite the user's edits.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset]);

  // Native folder picker (desktop only): pick a generation folder anywhere on
  // disk -- not just the ones the backend found under the canonical temp dir --
  // then resolve it through the same manifest path as the dropdown.
  const handleBrowseGenerationFolder = async (): Promise<void> => {
    const result = await selectFolder();
    if (result.status === 'selected') {
      await handleSelectGeneration(result.path);
    } else if (result.status === 'error') {
      setGenerationLoadError(t('youtube.publish.fromGeneration.folderPickerError'));
    }
    // 'cancelled' and 'unavailable' are silent — no action required
  };

  // Native file picker (desktop only): point the "Video file path" field at an
  // .mp4 anywhere on disk instead of typing the path by hand.
  const handleBrowseVideoFile = async (): Promise<void> => {
    setVideoFilePickerError(null);
    const result = await selectVideoFile();
    if (result.status === 'selected') {
      setVideoFilePath(result.path);
    } else if (result.status === 'error') {
      setVideoFilePickerError(t('youtube.publish.fields.videoFilePickerError'));
    }
    // 'cancelled' and 'unavailable' are silent — no action required
  };

  // ---------------------------------------------------------------------------
  // Channel video picker — selecting a card auto-fills URL + title + description.
  // Fields stay editable; edits flow into the localization/translation step.
  // ---------------------------------------------------------------------------
  const handleSelectVideo = (video: YouTubeChannelVideo): void => {
    setVideoUrl(video.url);
    setSelectedVideoId(video.video_id);
    setSourceTitle(video.title);
    setSourceDescription(video.description);
    // Auto-fill the source language from the video's declared AUDIO language
    // only -- that's the one reliable signal for STT/caption-track matching.
    // declared_language (metadata/title-description text language) is NOT the
    // same thing and must never be used as a stand-in for it: a video can be
    // shot in Portuguese with an English metadata language for reach, which
    // would otherwise silently point caption/STT lookups at the wrong
    // language. Fall back to auto-detect, never a guessed language.
    setSourceLanguage(video.default_audio_language || 'auto');
    // Source metadata changed -> drop any reused per-language SEO overrides.
    setSeoEdits({});
    // Fetch which languages already exist on this video (badges + gap focus).
    // Note: status.default_language is NOT used to fill sourceLanguage here --
    // resolve_video_metadata() falls back to a hardcoded "en" when the video
    // declares neither a metadata nor an audio language, which would silently
    // defeat auto-detect for exactly the videos that need it most.
    setVideoLangStatus(null);
    if (channel_id) {
      void getVideoLanguageStatus(channel_id, video.url)
        .then((status) => {
          setVideoLangStatus(status);
        })
        .catch(() => {
          // Non-fatal: badges simply won't appear.
        });
    }
  };

  // ---------------------------------------------------------------------------
  // Optional audio PREVIEW (narration_source='audio', desktop only).
  // Downloads audio -> speech-to-text -> SEO and fills the editable source fields
  // so the user can review/edit them before publishing. The same generation runs
  // inside the publish job when no preview is used; previewing here fills the
  // fields and switches the source to 'none' so the job publishes the reviewed
  // values instead of regenerating.
  // ---------------------------------------------------------------------------
  const handleGenerateFromVideo = (): void => {
    if (!videoUrl || aiGenerating) return;
    setAiGenerating(true);
    setAiError(null);
    setAiMessage(t('youtube.publish.aiGenerate.running'));

    void (async () => {
      try {
        const { job_id } = await generateMetadataFromVideo({
          channel_id,
          video_url: videoUrl,
          source_language: effectiveSourceLanguage,
        });
        aiUnsubscribeRef.current = subscribeToMetadataFromVideoEvents(job_id, (ev) => {
          if (ev.type === 'progress') {
            setAiMessage(ev.message ?? null);
          } else if (ev.type === 'completed' && ev.result) {
            setSourceTitle(ev.result.title);
            setSourceDescription(ev.result.description);
            // If the AI detected a concrete audio language, adopt it as source.
            if (ev.result.detected_language) {
              setSourceLanguage(ev.result.detected_language);
            }
            // Source metadata changed -> drop any reused per-language SEO overrides.
            setSeoEdits({});
            // The base SEO now lives in the editable fields; publish them as-is
            // (don't regenerate during the job).
            setNarrationSource('none');
            setAiGenerating(false);
            setAiMessage(null);
            aiUnsubscribeRef.current?.();
            aiUnsubscribeRef.current = null;
          } else if (ev.type === 'failed') {
            setAiError(ev.error ?? t('youtube.publish.aiGenerate.error'));
            setAiGenerating(false);
            setAiMessage(null);
            aiUnsubscribeRef.current?.();
            aiUnsubscribeRef.current = null;
          }
        });
      } catch (err) {
        setAiError(err instanceof Error ? err.message : t('youtube.publish.aiGenerate.error'));
        setAiGenerating(false);
        setAiMessage(null);
      }
    })();
  };

  // Close any open SSE streams on unmount.
  useEffect(() => {
    return () => {
      aiUnsubscribeRef.current?.();
      aiUnsubscribeRef.current = null;
    };
  }, []);

  // Reset the narration source to the per-mode default when the mode changes.
  // edit_existing: audio on desktop (disabled on web -> captions); end_to_end:
  // 'none' until a generation is picked (handleSelectGeneration opts it in).
  useEffect(() => {
    setNarrationSource(mode === 'edit_existing' ? (isDesktopApp() ? 'audio' : 'captions') : 'none');
  }, [mode]);

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------
  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      const targetLanguages: YouTubeLanguageConfig[] = selectedLangCodes.map((code, idx) => {
        const edit = seoEdits[code];
        // end_to_end captions come from the run folder's existing SRT files; we
        // NEVER regenerate them unless the user opted in via "Regenerate subtitles"
        // for this language (then we translate the run's source SRT fresh ->
        // 'pipeline_translate', which also covers languages the run never produced).
        // When neither applies and the run has no SRT for this language (e.g. an
        // audio-only language in the audio+subtitle union), skip captions -- SEO
        // still applies. The preset path (no manifest) keeps pipeline_generated.
        const wantRegenerate = regenerateSubtitles && regenerateLangCodes.includes(code);
        const endToEndCaptions = wantRegenerate
          ? 'pipeline_translate'
          : jobAssetsRoot && (!pickedManifest || pickedManifest.captions[code])
            ? 'pipeline_generated'
            : 'skip';
        const captionsSource = !applyCaptions
          ? 'skip'
          : mode === 'edit_existing'
            ? 'translate_from_source'
            : endToEndCaptions;
        return {
          language_code: code,
          enable_title_description: applySeo,
          enable_captions: applyCaptions && captionsSource !== 'skip',
          enable_thumbnail: applyThumbnail && idx === 0, // GLOBAL: only first language (YouTube = one thumbnail per video)
          enable_audio: false,
          asset_source_audio: 'skip',
          asset_source_captions: captionsSource,
          asset_source_thumbnail: 'skip',
          title_override: applySeo ? (edit?.title ?? null) : null,
          // Hashtags are always appended to the localized description when SEO is
          // on (the SEO model generates them as part of the description).
          description_override:
            applySeo && edit
              ? [edit.description, edit.hashtags].filter(Boolean).join('\n\n')
              : null,
        };
      });

      const request: YouTubePublishRequest = {
        mode,
        source_language: effectiveSourceLanguage,
        source_title: sourceTitle || undefined,
        source_description: sourceDescription || undefined,
        target_languages: targetLanguages,
        // Re-upload captions for languages already on the video (overwrite the
        // existing track) when the user opts in; only meaningful for edit_existing,
        // where the destination video can already have caption tracks. Omitted
        // otherwise so the backend keeps its default "skip".
        on_existing_locale:
          mode === 'edit_existing' && applyCaptions && overwriteCaptions ? 'overwrite' : undefined,
        // Where the publish job derives the base title/description from (backend
        // Step 5a): 'audio' = STT of the video's audio (edit_existing, desktop),
        // 'captions' = the published caption track (edit_existing) or the run
        // folder's narration (end_to_end), 'none' = use the fields verbatim.
        narration_source: narrationSource,
        // The chosen model drives the publish job's per-language localization
        // AND caption translation (both share this one model choice). Only
        // relevant when SEO/captions are enabled AND the user opted into paid
        // AI translation (useAiTranslation) -- NLLB (the default) needs none.
        seo_model:
          (applySeo || applyCaptions) && useAiTranslation ? seoModel || undefined : undefined,
        // Provider for seo_model above (openrouter/ollama/runpod) -- shares the
        // same gating as seo_model since it's meaningless without a model choice.
        llm_provider: (applySeo || applyCaptions) && useAiTranslation ? llmProvider : undefined,
        // Governs BOTH title/description localization and caption translation.
        // Omitted when SEO/captions aren't applied at all so the backend keeps
        // its own default; otherwise reflects the toggle below ('llm' requires
        // seo_model, enforced by submitDisabled above).
        translation_method:
          applySeo || applyCaptions ? (useAiTranslation ? 'llm' : 'nllb') : undefined,
        video_url: mode !== 'end_to_end' ? videoUrl || undefined : undefined,
        upload_metadata:
          mode === 'end_to_end'
            ? {
                video_file_path: videoFilePath,
                // default_language intentionally omitted: the backend derives it
                // from the (auto-resolved) source_language. No redundant UI field.
                visibility,
                category_id: categoryId,
                audience,
                // tags intentionally omitted: the SEO model chooses them from the
                // narration (applied server-side at upload). No manual tags field.
              }
            : undefined,
      };

      const result = await submitPublishJob({
        channel_id,
        // Send the run/assets folder whenever we have one. For end_to_end it is
        // the generation folder (captions + narration live there) — required for
        // pipeline_generated captions and SEO-from-narration. If we don't have it,
        // the backend derives it from the uploaded video's parent folder.
        ...(jobAssetsRoot ? { job_assets_root: jobAssetsRoot } : {}),
        request,
      });

      setSubmitResult(result);
      onJobStarted(result.job_id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : t('youtube.publish.submitError'));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={(e) => void handleSubmit(e)} className="space-y-6">
      {/* Preset loaded banner */}
      {showPresetBanner && (
        <div className="flex items-center justify-between bg-purple-900/20 border border-purple-500/30 text-purple-200 text-sm px-3 py-2 rounded">
          <span>{t('youtube.publish.preset.loadedFromPipeline')}</span>
          <button
            type="button"
            onClick={() => setShowPresetBanner(false)}
            className="ml-2 text-purple-300 hover:text-white transition-colors"
            aria-label="Dismiss"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Mode */}
      <SegmentedPicker
        label={t('youtube.publish.mode')}
        size="sm"
        layout="equal"
        options={MODES.map(({ value, labelKey }) => ({ value, label: t(labelKey) }))}
        value={mode}
        onChange={(value) => onModeChange(value as YouTubePublishMode)}
      />

      {/* Publish from a finished generation — end_to_end only.
          The dropdown lists runs the backend found under the canonical temp
          dir; the desktop-only "Procurar pasta…" button lets the user pick a
          generation folder anywhere on disk (e.g. the project's own temp/). */}
      {mode === 'end_to_end' &&
        (recentGenerations.length > 0 || isDesktopApp() || recentGenerationsLoadError !== null) && (
          <div className="space-y-2">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <label className="text-white/70 text-sm">
                {t('youtube.publish.fromGeneration.label')}
              </label>
              {isDesktopApp() && (
                <button
                  type="button"
                  onClick={() => void handleBrowseGenerationFolder()}
                  className="min-h-[44px] px-3 rounded bg-black/30 hover:bg-white/10 border border-white/10 text-white/70 text-sm font-medium transition-colors flex items-center gap-2 shrink-0"
                >
                  <FolderOpen className="h-4 w-4" />
                  {t('youtube.publish.fromGeneration.browseFolder')}
                </button>
              )}
            </div>
            {recentGenerationsLoadError !== null && (
              <p className="text-amber-400 text-xs">
                {t('youtube.publish.fromGeneration.loadListError')}
              </p>
            )}
            {recentGenerations.length > 0 && (
              <select
                value={selectedGenerationDir}
                onChange={(e) => handleSelectGeneration(e.target.value)}
                className="w-full bg-black/30 border border-white/10 rounded px-3 py-2 text-white min-h-[44px]"
              >
                <option value="">{t('youtube.publish.fromGeneration.selectRecent')}</option>
                {recentGenerations.map((g) => (
                  <option key={g.run_dir} value={g.run_dir}>
                    {`${g.title || g.run_id} · ${t(
                      'youtube.publish.fromGeneration.subtitlesCount',
                      {
                        count: g.subtitle_languages.length,
                      }
                    )}${g.has_seo ? ` · ${t('youtube.publish.fromGeneration.seoTag')}` : ''}`}
                  </option>
                ))}
              </select>
            )}
            {selectedGenerationDir &&
              !recentGenerations.some((g) => g.run_dir === selectedGenerationDir) && (
                <p className="text-white/50 text-xs break-all">
                  {t('youtube.publish.fromGeneration.selectedFolder')}{' '}
                  <span className="text-white/70">{selectedGenerationDir}</span>
                </p>
              )}
            {recentGenerationsLoadError && (
              <p className="text-red-400 text-xs">{recentGenerationsLoadError}</p>
            )}
            {generationLoadError && <p className="text-red-400 text-xs">{generationLoadError}</p>}

            {pickedManifest && (
              <div className="space-y-2">
                {pickedManifest.validation.errors.length > 0 && (
                  <div className="rounded border border-red-500/40 bg-red-900/20 p-2 space-y-1">
                    <p className="text-red-300 text-xs font-medium">
                      {t('youtube.publish.fromGeneration.notStandard')}
                    </p>
                    <ul className="list-disc list-inside text-red-300/90 text-xs space-y-0.5">
                      {pickedManifest.validation.errors.map((msg) => (
                        <li key={msg}>{msg}</li>
                      ))}
                    </ul>
                  </div>
                )}
                {pickedManifest.validation.warnings.length > 0 && (
                  <ul className="list-disc list-inside text-amber-300/90 text-xs space-y-0.5">
                    {pickedManifest.validation.warnings.map((msg) => (
                      <li key={msg}>{msg}</li>
                    ))}
                  </ul>
                )}
                {pickedManifest.validation.valid_for_seo && (
                  <label className="flex items-start gap-2 text-sm text-white/80 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={narrationSource !== 'none'}
                      onChange={(e) => setNarrationSource(e.target.checked ? 'captions' : 'none')}
                      className="mt-0.5 min-h-[18px] min-w-[18px]"
                    />
                    <span className="flex items-center gap-1.5">
                      <Sparkles className="w-4 h-4 text-purple-400" />
                      {t('youtube.publish.fromGeneration.generateBaseSeo')}
                      {pickedManifest.has_seo && (
                        <span className="text-green-400/80 text-xs">
                          {t('youtube.publish.fromGeneration.alreadyHasSeo')}
                        </span>
                      )}
                    </span>
                  </label>
                )}
              </div>
            )}

            <p className="text-white/40 text-xs">{t('youtube.publish.fromGeneration.note')}</p>
          </div>
        )}

      {/* Video file path — end_to_end only */}
      {mode === 'end_to_end' && (
        <div className="space-y-1">
          <label className="text-white/70 text-sm">
            {t('youtube.publish.fields.videoFilePath')} *
          </label>
          <div className="flex gap-2">
            <input
              type="text"
              required
              value={videoFilePath}
              onChange={(e) => setVideoFilePath(e.target.value)}
              placeholder="/home/.../video.mp4"
              className="input w-full"
            />
            {isDesktopApp() && (
              <button
                type="button"
                onClick={() => void handleBrowseVideoFile()}
                className="min-h-[44px] px-3 rounded bg-black/30 hover:bg-white/10 border border-white/10 text-white/70 text-sm font-medium transition-colors flex items-center gap-2 shrink-0"
                title={t('youtube.publish.fields.browseVideoFileTitle')}
              >
                <FolderOpen className="h-4 w-4" />
                {t('youtube.publish.fields.browseVideoFile')}
              </button>
            )}
          </div>
          {videoFilePickerError && <p className="text-red-400 text-xs">{videoFilePickerError}</p>}
        </div>
      )}

      {/* Channel video picker + URL — edit_existing only */}
      {mode !== 'end_to_end' && (
        <div className="space-y-3">
          {channel_id && (
            <ChannelVideoPicker
              channel_id={channel_id}
              selectedVideoId={selectedVideoId}
              onSelect={handleSelectVideo}
            />
          )}
          <div className="space-y-1">
            <label className="text-white/70 text-sm">{t('youtube.publish.picker.urlLabel')}</label>
            <input
              type="url"
              required
              value={videoUrl}
              onChange={(e) => {
                setVideoUrl(e.target.value);
                // Manual edit detaches the selection so the grid highlight stays truthful.
                setSelectedVideoId(null);
              }}
              placeholder="https://www.youtube.com/watch?v=..."
              className="input w-full"
            />
            <p className="text-white/40 text-xs">{t('youtube.publish.picker.urlHint')}</p>
          </div>
        </div>
      )}

      {/* Source metadata */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-1">
          <label className="text-white/70 text-sm">{t('youtube.publish.fields.sourceTitle')}</label>
          <input
            type="text"
            value={sourceTitle}
            onChange={(e) => setSourceTitle(e.target.value)}
            className="input w-full"
          />
        </div>
        <div className="space-y-1">
          <label className="text-white/70 text-sm">
            {t('youtube.publish.sourceLanguage.label')}
          </label>
          {languages.length > 0 ? (
            <YouTubeLanguageSelector
              languages={languages}
              values={sourceLanguage ? [sourceLanguage] : []}
              onChange={(codes) => setSourceLanguage(codes[0] ?? 'auto')}
              maxSelection={1}
              showAutoDetect
            />
          ) : (
            <p className="text-white/40 text-xs">{t('common.loading')}</p>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-white/70 text-sm">
          {t('youtube.publish.fields.sourceDescription')}
        </label>
        <textarea
          value={sourceDescription}
          onChange={(e) => setSourceDescription(e.target.value)}
          rows={3}
          className="input w-full resize-none"
        />
      </div>

      {/* Base title/description source — edit_existing only. The publish job
          generates it from this source (then localizes), so publishing "just
          works". 'audio' is desktop-only (yt-dlp download); on web it is disabled
          and 'captions' is the default. */}
      {mode === 'edit_existing' && videoUrl && (
        <div className="space-y-3 p-4 bg-black/20 rounded-lg border border-white/10">
          <p className="text-white/80 text-sm font-medium flex items-center gap-1.5">
            <Sparkles className="w-4 h-4 text-purple-400" />
            {t('youtube.publish.narrationSource.label')}
          </p>

          <div className="space-y-2">
            {/* Audio (STT) — desktop only */}
            <label
              className={`flex items-start gap-2 text-sm ${
                isDesktopApp() ? 'text-white/80 cursor-pointer' : 'text-white/40 cursor-not-allowed'
              }`}
            >
              <input
                type="radio"
                name="narrationSource"
                checked={narrationSource === 'audio'}
                disabled={!isDesktopApp()}
                onChange={() => setNarrationSource('audio')}
                className="mt-0.5 min-h-[18px] min-w-[18px]"
              />
              <span className="flex flex-col">
                <span>{t('youtube.publish.narrationSource.audio')}</span>
                <span className="text-white/40 text-xs">
                  {isDesktopApp()
                    ? t('youtube.publish.narrationSource.audioNote')
                    : t('youtube.publish.narrationSource.audioDesktopOnly')}
                </span>
              </span>
            </label>

            {/* Published captions track */}
            <label className="flex items-start gap-2 text-sm text-white/80 cursor-pointer">
              <input
                type="radio"
                name="narrationSource"
                checked={narrationSource === 'captions'}
                onChange={() => setNarrationSource('captions')}
                className="mt-0.5 min-h-[18px] min-w-[18px]"
              />
              <span className="flex flex-col">
                <span>{t('youtube.publish.narrationSource.captions')}</span>
                <span className="text-white/40 text-xs">
                  {t('youtube.publish.narrationSource.captionsNote')}
                </span>
              </span>
            </label>

            {/* Don't generate — use the fields verbatim */}
            <label className="flex items-start gap-2 text-sm text-white/80 cursor-pointer">
              <input
                type="radio"
                name="narrationSource"
                checked={narrationSource === 'none'}
                onChange={() => setNarrationSource('none')}
                className="mt-0.5 min-h-[18px] min-w-[18px]"
              />
              <span className="flex flex-col">
                <span>{t('youtube.publish.narrationSource.none')}</span>
                <span className="text-white/40 text-xs">
                  {t('youtube.publish.narrationSource.noneNote')}
                </span>
              </span>
            </label>
          </div>

          {/* Optional: preview the audio-generated title/description before
              publishing (desktop only). Fills the editable fields above and
              switches the source to 'none' so the job publishes the reviewed
              values instead of regenerating. */}
          {narrationSource === 'audio' && isDesktopApp() && (
            <div className="space-y-2 pt-1 border-t border-white/10">
              <button
                type="button"
                disabled={aiGenerating}
                onClick={handleGenerateFromVideo}
                className="flex items-center justify-center gap-2 min-h-[44px] w-full sm:w-auto px-4 py-2 rounded-lg bg-purple-600/80 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
              >
                <Sparkles className="h-4 w-4" />
                {t('youtube.publish.aiGenerate.previewButton')}
              </button>
              <p className="text-white/40 text-xs">{t('youtube.publish.aiGenerate.previewNote')}</p>
              {aiGenerating && aiMessage && (
                <p className="text-purple-300 text-xs flex items-center gap-2">
                  <span className="inline-block h-3 w-3 rounded-full border-2 border-purple-400 border-t-transparent animate-spin" />
                  {aiMessage}
                </p>
              )}
              {aiError && (
                <p className="text-red-400 text-xs" role="alert">
                  {aiError}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      {/* Upload metadata — end_to_end only */}
      {mode === 'end_to_end' && (
        <div className="space-y-4 p-4 bg-black/20 rounded-lg border border-white/10">
          <p className="text-white/70 text-xs uppercase tracking-wide">
            {t('youtube.publish.upload.sectionTitle')}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <CompactSelect
              label={t('youtube.publish.upload.category')}
              options={YOUTUBE_VIDEO_CATEGORIES.map((c) => ({ value: c.id, label: c.name }))}
              value={categoryId}
              onChange={setCategoryId}
              searchable
            />
          </div>

          {/* Visibility */}
          <SegmentedPicker
            label={t('youtube.publish.upload.visibility')}
            size="sm"
            layout="equal"
            options={VISIBILITIES.map((v) => ({
              value: v,
              label: t(
                `youtube.publish.upload.visibility${v.charAt(0).toUpperCase()}${v.slice(1)}`
              ),
            }))}
            value={visibility}
            onChange={(v) => setVisibility(v as 'private' | 'unlisted' | 'public')}
          />

          {/* Audience */}
          <SegmentedPicker
            label={t('youtube.publish.upload.audience')}
            size="sm"
            layout="equal"
            options={AUDIENCES.map((a) => ({
              value: a,
              label:
                a === 'not_kids'
                  ? t('youtube.publish.upload.audienceNotKids')
                  : t('youtube.publish.upload.audienceKids'),
            }))}
            value={audience}
            onChange={(a) => setAudience(a as 'kids' | 'not_kids')}
          />
        </div>
      )}

      {/* Target languages */}
      <div className="space-y-2">
        <p className="text-white/70 text-sm font-medium">
          {t('youtube.publish.fields.targetLanguages')}
        </p>
        {languages.length === 0 ? (
          <p className="text-white/40 text-xs">{t('common.loading')}</p>
        ) : (
          <YouTubeLanguageSelector
            languages={languages}
            values={selectedLangCodes}
            onChange={setSelectedLangCodes}
            existingCaptionLanguages={videoLangStatus?.captions}
            existingLocalizationLanguages={videoLangStatus?.localizations}
          />
        )}
      </div>

      {/* What to apply — all checked by default, shown once a language is selected */}
      {selectedLangCodes.length > 0 && (
        <fieldset className="space-y-3 p-4 bg-black/20 rounded-lg border border-white/10">
          <legend className="text-white/70 text-sm font-medium px-1">
            {t('youtube.publish.apply.sectionTitle')}
          </legend>

          <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
            <input
              type="checkbox"
              checked={applySeo}
              onChange={(e) => setApplySeo(e.target.checked)}
              className="w-4 h-4 accent-purple-500"
            />
            <span className="text-white/80 text-sm">{t('youtube.publish.apply.seo')}</span>
          </label>

          {(applySeo || applyCaptions) && (
            <label className="flex items-start gap-3 min-h-[44px] cursor-pointer pl-7">
              <input
                type="checkbox"
                checked={useAiTranslation}
                onChange={(e) => setUseAiTranslation(e.target.checked)}
                className="w-4 h-4 mt-1 accent-purple-500"
              />
              <span className="text-white/70 text-xs">
                {t('youtube.publish.apply.useAiTranslation')}
                <span className="block text-white/40">
                  {t('youtube.publish.apply.useAiTranslationNote')}
                </span>
              </span>
            </label>
          )}

          <div className="space-y-1">
            <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
              <input
                type="checkbox"
                checked={applyCaptions}
                onChange={(e) => setApplyCaptions(e.target.checked)}
                className="w-4 h-4 accent-purple-500"
              />
              <span className="text-white/80 text-sm">{t('youtube.publish.apply.captions')}</span>
            </label>
            {applyCaptions && mode === 'edit_existing' && (
              <p className="text-white/40 text-xs pl-7">
                {t('youtube.publish.apply.captionsFromSourceNote')}
              </p>
            )}
            {applyCaptions && mode === 'edit_existing' && (
              <label className="flex items-start gap-3 min-h-[44px] cursor-pointer pl-7">
                <input
                  type="checkbox"
                  checked={overwriteCaptions}
                  onChange={(e) => setOverwriteCaptions(e.target.checked)}
                  className="w-4 h-4 mt-1 accent-purple-500"
                />
                <span className="text-white/70 text-xs">
                  {t('youtube.publish.apply.overwriteCaptions')}
                  <span className="block text-white/40">
                    {t('youtube.publish.apply.overwriteCaptionsNote')}
                  </span>
                </span>
              </label>
            )}

            {/* Regenerate subtitles (end_to_end): existing run SRTs are reused
                as-is; opting in re-translates the run's source subtitle into the
                chosen languages (also lets you publish languages the run never
                produced). */}
            {applyCaptions && mode === 'end_to_end' && (
              <div className="pl-7 space-y-2">
                <label className="flex items-start gap-3 min-h-[44px] cursor-pointer">
                  <input
                    type="checkbox"
                    checked={regenerateSubtitles}
                    onChange={(e) => setRegenerateSubtitles(e.target.checked)}
                    className="w-4 h-4 mt-1 accent-purple-500"
                  />
                  <span className="text-white/70 text-xs">
                    {t('youtube.publish.apply.regenerateSubtitles', {
                      defaultValue: 'Regenerate subtitles for some languages',
                    })}
                    <span className="block text-white/40">
                      {t('youtube.publish.apply.regenerateSubtitlesNote', {
                        defaultValue:
                          'Re-translates the source subtitle into the chosen languages instead of reusing existing files. Use it for languages the run did not produce.',
                      })}
                    </span>
                  </span>
                </label>
                {regenerateSubtitles && languages.length > 0 && (
                  <YouTubeLanguageSelector
                    languages={languages.filter((l) => selectedLangCodes.includes(l.code))}
                    values={regenerateLangCodes}
                    onChange={setRegenerateLangCodes}
                  />
                )}
              </div>
            )}
          </div>

          <div className="space-y-1">
            <label className="flex items-center gap-3 min-h-[44px] cursor-pointer">
              <input
                type="checkbox"
                checked={applyThumbnail}
                onChange={(e) => setApplyThumbnail(e.target.checked)}
                className="w-4 h-4 accent-purple-500"
              />
              <span className="text-white/80 text-sm">{t('youtube.publish.apply.thumbnail')}</span>
            </label>
            {applyThumbnail && (
              <p className="text-white/40 text-xs pl-7">
                {t('youtube.publish.apply.thumbnailGlobalNote')}
              </p>
            )}
          </div>
        </fieldset>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Translation engine note — shown whenever SEO/captions run but the   */}
      {/* user has NOT opted into paid AI translation: NLLB (free, local) is  */}
      {/* used instead, and no model choice is needed.                        */}
      {/* ------------------------------------------------------------------ */}
      {selectedLangCodes.length > 0 && (applySeo || applyCaptions) && !useAiTranslation && (
        <p className="text-white/40 text-xs px-4">
          {t('youtube.publish.translationMethod.freeNllbNote')}
        </p>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* SEO/captions LLM provider + model — REQUIRED when SEO localization  */}
      {/* or captions translation is on AND the user opted into paid AI       */}
      {/* translation. The user must pick a model explicitly (no              */}
      {/* auto-selection); it drives the publish job's per-language            */}
      {/* title/description localization AND caption translation.              */}
      {/* ------------------------------------------------------------------ */}
      {selectedLangCodes.length > 0 && (applySeo || applyCaptions) && useAiTranslation && (
        <div className="space-y-2 p-4 bg-black/20 rounded-lg border border-white/10">
          <CompactSelect
            label={t('youtube.publish.seoModel.providerLabel', { defaultValue: 'LLM Provider' })}
            options={llmProviderOptions}
            value={llmProvider}
            onChange={handleLlmProviderChange}
            testId="youtube-publish-llm-provider-select"
          />
          <LLMModelSelector
            models={
              seoModels.length === 0
                ? [{ value: '', label: t('common.loading'), disabled: true }]
                : seoModels
            }
            value={seoModel}
            onChange={handleSeoModelChange}
            disabled={seoModels.length === 0}
            label={t('youtube.publish.seoModel.modelLabel')}
            showFreeOnly={false}
            autoSelectCheapest={false}
            recommendedValue={RECOMMENDED_SEO_MODEL}
          />
          {seoModel ? (
            <p className="text-white/40 text-xs">{t('youtube.publish.seoModel.autoNote')}</p>
          ) : (
            seoModels.length > 0 && (
              <p className="text-amber-300/80 text-xs">
                {t('youtube.publish.seoModel.pickModelHint')}
              </p>
            )
          )}

          {/* Cost preview: title+description localization only (see
              translationEstimateParams comment for why captions aren't included). */}
          <TokenEstimateCard
            operationPath="/api/v1/youtube/publish"
            parameters={translationEstimateParams}
            onEstimateChange={({ status }) => setTranslationCostGate(status)}
          />
        </div>
      )}

      {applyCaptions && useAiTranslation && (
        <p className="text-white/40 text-xs px-4">
          {t('youtube.publish.translationMethod.captionsCostNote')}
        </p>
      )}

      {/* ------------------------------------------------------------------ */}
      {/* Thumbnail panel (only when languages selected AND thumbnail opted-in) */}
      {/* ------------------------------------------------------------------ */}
      {selectedLangCodes.length > 0 && applyThumbnail && (
        <div className="border border-white/10 rounded-lg overflow-hidden">
          {/* Collapsible header */}
          <button
            type="button"
            onClick={() => setThumbPanelOpen((v) => !v)}
            className="w-full flex items-center justify-between px-4 py-3 bg-black/20 hover:bg-black/30 transition-colors text-sm text-white/80"
          >
            <span className="font-medium">{t('youtube.publish.thumbnail.panelTitle')}</span>
            {thumbPanelOpen ? (
              <ChevronUp className="h-4 w-4 text-white/40" />
            ) : (
              <ChevronDown className="h-4 w-4 text-white/40" />
            )}
          </button>

          {thumbPanelOpen && (
            <div className="p-4 bg-black/10">
              {/*
                Rich thumbnail panel: the SAME model-aware image config + progress
                + generic error as Posts & Carousels (shared ImageGeneratorPanel).
                The concept comes from the primary language's localized title /
                description when available, else the source title/description
                (one thumbnail per video).
              */}
              <YouTubeThumbnailPanel
                title={seoEdits[selectedLangCodes[0]]?.title || sourceTitle}
                description={seoEdits[selectedLangCodes[0]]?.description || sourceDescription}
                jobAssetsRoot={jobAssetsRoot}
                onJobAssetsRootChange={setJobAssetsRoot}
                onThumbnailSaved={setThumbnailSavedPath}
                primaryLanguage={selectedLangCodes[0] || sourceLanguage}
                // One thumbnail serves every language: overlay the title only for a
                // single-language publish; multilingual (>1) -> text-free image.
                includeText={selectedLangCodes.length <= 1}
              />
            </div>
          )}
        </div>
      )}

      {/* Submit result banner */}
      {submitResult && (
        <div className="bg-green-900/30 border border-green-500/30 rounded-lg px-4 py-3 text-sm text-green-300">
          <p>
            {t('youtube.publish.jobStarted')}{' '}
            <code className="text-green-200">{submitResult.job_id}</code>
          </p>
          <p className="text-green-400/70 text-xs mt-1">
            {t('youtube.publish.estimatedUnits', {
              units: String(submitResult.estimated_units),
            })}
          </p>
        </div>
      )}

      {/* Custom thumbnail is enabled but none was generated. The publish job
          uploads no thumbnail (it never generates one server-side), so warn —
          but don't block: the user may intend to publish without one. */}
      {applyThumbnail &&
        selectedLangCodes.length > 0 &&
        !thumbnailSavedPath &&
        Object.keys(pickedManifest?.thumbnails ?? {}).length === 0 && (
          <p className="text-amber-300/80 text-xs">
            {t('youtube.publish.thumbnail.enabledButNotGenerated')}
          </p>
        )}

      {wouldRepublishNoop && (
        <p className="text-amber-300/90 text-xs" role="alert">
          {t('youtube.publish.noopWarning')}
        </p>
      )}

      {error && <p className="text-red-400 text-sm">{error}</p>}

      <button
        type="submit"
        disabled={submitDisabled}
        className="min-h-[44px] w-full sm:w-auto px-6 py-2 rounded-lg bg-purple-600/80 hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium transition-colors"
      >
        {submitting ? t('common.processing') : t('youtube.publish.submit')}
      </button>
    </form>
  );
}
